import React, { useMemo, useState } from "react";
import { graphql, Link } from "gatsby";
import { getImage, GatsbyImage } from "gatsby-plugin-image";
import Seo from "gatsby-plugin-wpgraphql-seo";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import { Banner, Layout } from "../components";
import SearchField from "../components/Form/SearchField";
const Page = ({
  data: {
    wpPage,
    wpPage: { pageMeta },
    allWpPost,
    sticky,
  },
}) => {
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const postsPerPage = 10;
  // Filter posts based on search input
  const filteredPosts = useMemo(
    () =>
      search
        ? allWpPost.nodes.filter(
            (article) =>
              !article.isSticky &&
              article.title.toLowerCase().includes(search.toLowerCase())
          )
        : allWpPost.nodes.filter((article) => article.isSticky === false),
    [search, allWpPost.nodes]
  );
  // Calculate the total number of posts after filtering
  const totalPosts = filteredPosts.length;
  // Calculate the total number of pages
  const totalPages = Math.ceil(totalPosts / postsPerPage);
  // Calculate the start and end index for the current page
  const indexOfLastPost = currentPage * postsPerPage;
  const indexOfFirstPost = indexOfLastPost - postsPerPage;
  // Get the current posts for the current page
  const currentPosts = filteredPosts.slice(indexOfFirstPost, indexOfLastPost);
  // Handle page change
  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };
  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  return (
    <>
      <Seo post={wpPage} />
      <Layout>
        <Banner banner={pageMeta?.pageBanner} />
        <section className="overflow-hidden bg-white py-12">
          <div className="container mx-auto">
            <div className="flex flex-wrap">
              <div className="w-full basis-full px-4">
                <h2 className="mb-8 text-center font-openSans text-4xl capitalize text-secondary">
                  Featured Posts
                </h2>
              </div>
              <div className="w-full basis-full px-4">
                <Swiper
                  slidesPerView={1}
                  spaceBetween={30}
                  pagination={{
                    clickable: true,
                  }}
                  breakpoints={{
                    1024: {
                      slidesPerView: 3,
                      spaceBetween: 30,
                    },
                  }}
                  loop={true}
                  navigation={true}
                  modules={[Navigation, Pagination]}
                  className="pb-10"
                >
                  {sticky?.nodes.map((item, index) => {
                    return (
                      <SwiperSlide key={index}>
                        <h3 className="font-openSans text-xl text-primary">
                          <Link to={item.uri}>{item.title}</Link>
                        </h3>
                        <p
                          className="font-openSans text-xl text-black"
                          dangerouslySetInnerHTML={{
                            __html: item.excerpt,
                          }}
                        ></p>
                        <Link
                          to={item.uri}
                          className="font-openSans text-xl text-secondary underline"
                        >
                          Continue Reading
                        </Link>
                      </SwiperSlide>
                    );
                  })}
                </Swiper>
              </div>
            </div>
          </div>
        </section>
        <section className="overflow-hidden bg-white py-12">
          <div className="container mx-auto">
            <div className="mb-6 flex w-full items-center justify-end gap-6 px-4">
              <SearchField
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
            <div className="flex flex-wrap">
              {currentPosts?.length > 0 ? (
                currentPosts?.map((item, index) => {
                  const image = getImage(
                    item?.featuredImage?.node?.localFile?.childImageSharp
                      .gatsbyImageData
                  );
                  return (
                    <div
                      className="mb-8 w-full basis-full px-4 lg:w-1/2 lg:basis-1/2"
                      key={index}
                    >
                      {image && <GatsbyImage image={image} alt="" />}
                      <h3 className="mb-4 mt-4 font-openSans text-xl text-secondary">
                        <Link to={item.uri}>{item.title}</Link>
                      </h3>
                      <p
                        className="font-openSans text-lg text-black"
                        dangerouslySetInnerHTML={{
                          __html: item.excerpt,
                        }}
                      ></p>
                      <Link
                        to={item.uri}
                        className="mt-4 inline-block bg-secondary px-8 py-2 font-openSans text-xl text-white transition-all duration-500 hover:bg-primary"
                      >
                        Read More
                      </Link>
                    </div>
                  );
                })
              ) : (
                <span className="px-4 font-openSans">
                  No posts found matching "{search}".
                </span>
              )}
            </div>
            <div className="flex justify-between px-4">
              {currentPage === 1 ? null : (
                <button
                  onClick={handlePreviousPage}
                  disabled={currentPage === 1}
                  className="mt-4 inline-block bg-secondary px-8 py-2 font-openSans text-xl text-white transition-all duration-500 hover:bg-primary"
                >
                  &lt;&lt; Previous Page
                </button>
              )}
              {currentPage === totalPages || totalPosts === 0 ? null : (
                <button
                  onClick={handleNextPage}
                  disabled={currentPage === totalPages || totalPosts === 0}
                  className="mt-4 inline-block bg-secondary px-8 py-2 font-openSans text-xl text-white transition-all duration-500 hover:bg-primary"
                >
                  Next Page &gt;&gt;
                </button>
              )}
            </div>
          </div>
        </section>
      </Layout>
    </>
  );
};
export default Page;
export const query = graphql`
  query PageQuery {
    wpPage(databaseId: { eq: 692 }) {
      content
      pageMeta {
        pageBanner {
          desktopImage {
            localFile {
              childImageSharp {
                gatsbyImageData(layout: FULL_WIDTH, formats: WEBP)
              }
            }
          }
          mobileImage {
            localFile {
              childImageSharp {
                gatsbyImageData(layout: FULL_WIDTH, formats: WEBP)
              }
            }
          }
          captionTitle
          captionContent
          buttonLink
          buttonLabel
          showWhiteLogo
          whiteColor
          position
        }
      }
      seo {
        title
        metaDesc
        focuskw
        metaKeywords
        metaRobotsNoindex
        metaRobotsNofollow
        opengraphTitle
        opengraphDescription
        opengraphImage {
          altText
          sourceUrl
          srcSet
        }
        twitterTitle
        twitterDescription
        twitterImage {
          altText
          sourceUrl
          srcSet
        }
        canonical
        cornerstone
        schema {
          articleType
          pageType
          raw
        }
      }
    }
    sticky: allWpPost(
      sort: { date: DESC }
      filter: { isSticky: { eq: true } }
    ) {
      nodes {
        databaseId
        title
        uri
        excerpt
        featuredImage {
          node {
            localFile {
              childImageSharp {
                gatsbyImageData(layout: FULL_WIDTH, formats: WEBP)
              }
            }
          }
        }
      }
    }
    allWpPost(sort: { date: DESC }) {
      nodes {
        databaseId
        title
        uri
        excerpt
        isSticky
        featuredImage {
          node {
            localFile {
              childImageSharp {
                gatsbyImageData(layout: FULL_WIDTH, formats: WEBP)
              }
            }
          }
        }
      }
    }
  }
`;
